/**
 * Per-page SEO meta tag injection for SPA routes.
 *
 * Most social-media crawlers (FB, WhatsApp, Telegram, X, iMessage, LinkedIn,
 * Discord, Slack) DO NOT execute JavaScript when scraping a URL — so these
 * dynamic updates won't change the social preview directly. The right way to
 * share a kolv.lv resource for a rich preview is via the `/api/og/{kind}/{id}`
 * URL (see lib/share.js helpers), which renders server-side HTML.
 *
 * This helper still matters for:
 *   - Search engines (Google, Bing) that DO execute JS and pick up the meta
 *   - In-app browsers that show document.title in the address bar
 *   - Bookmark previews on macOS / iOS
 *   - JSON-LD structured data for Google rich snippets
 */

export function setPageMeta({ title, description, image, url, type = 'website', jsonLd = null }) {
  if (typeof document === 'undefined') return;

  if (title) document.title = title.length > 60 ? `${title.slice(0, 57)}…` : title;

  const ensureMeta = (selector, attrCreate, attrName, content) => {
    if (!content) return;
    let el = document.head.querySelector(selector);
    if (!el) {
      el = document.createElement('meta');
      Object.entries(attrCreate).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
    }
    el.setAttribute(attrName, content);
  };

  if (description) {
    ensureMeta('meta[name="description"]', { name: 'description' }, 'content', description);
  }

  ensureMeta('meta[property="og:title"]', { property: 'og:title' }, 'content', title);
  ensureMeta('meta[property="og:description"]', { property: 'og:description' }, 'content', description);
  ensureMeta('meta[property="og:type"]', { property: 'og:type' }, 'content', type);
  ensureMeta('meta[property="og:url"]', { property: 'og:url' }, 'content', url);
  ensureMeta('meta[property="og:image"]', { property: 'og:image' }, 'content', image);
  ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, 'content', title);
  ensureMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, 'content', description);
  ensureMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, 'content', image);

  if (url) {
    let canon = document.head.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement('link');
      canon.setAttribute('rel', 'canonical');
      document.head.appendChild(canon);
    }
    canon.setAttribute('href', url);
  }

  if (jsonLd) {
    let tag = document.getElementById('kolv-page-jsonld');
    if (!tag) {
      tag = document.createElement('script');
      tag.id = 'kolv-page-jsonld';
      tag.type = 'application/ld+json';
      document.head.appendChild(tag);
    }
    tag.textContent = JSON.stringify(jsonLd);
  }
}

export function resetPageMeta() {
  if (typeof document === 'undefined') return;
  document.title = 'KoLV - Ko Latvietis Vēlas';
  const origin = window.location.origin;
  const setBack = (sel, attr, val) => {
    const el = document.head.querySelector(sel);
    if (el) el.setAttribute(attr, val);
  };
  setBack('meta[property="og:type"]', 'content', 'website');
  setBack('meta[property="og:image"]', 'content', `${origin}/og-banner.png`);
  setBack('meta[name="twitter:image"]', 'content', `${origin}/og-banner.png`);
  const ld = document.getElementById('kolv-page-jsonld');
  if (ld) ld.remove();
}
